/**
 * Notification navigation helpers.
 *
 * Pure presentation/navigation logic — no business decisions, no writes.
 * Centralizes the rules used by the bell dropdown and `/notifications`:
 *
 *  - Internal links (start with `/`) → react-router navigate
 *  - External links (http/https)     → currently NOT auto-opened. Iteration 4
 *    keeps the safer default of ignoring external targets until we have a
 *    project-wide pattern for trusted external links.
 *  - Empty / invalid `action_url`    → fall back to a per-type default
 *    (Admins get a maintenance link for failed jobs; everyone else stays on
 *    the inbox). Never throws.
 */
import type { NavigateFunction } from "react-router-dom";

import type { NotificationInboxItem } from "@/services/notifications";

export interface NavigateOptions {
  /** Whether the current viewer holds an admin/super-admin role. */
  isAdmin?: boolean;
}

/** Resolve a safe internal target for a notification, or `null` for "do not navigate". */
export function resolveNotificationTarget(
  item: NotificationInboxItem,
  options: NavigateOptions = {},
): string | null {
  const raw = (item.action_url ?? "").trim();

  if (raw && raw.startsWith("/")) {
    return raw;
  }

  // External link: not auto-opened in Iteration 4. Fall through to defaults.
  // (Tracked as a future enhancement: trusted-domain allowlist.)

  switch (item.type) {
    case "buddy_request_received":
      return "/community/buddynet?tab=requests";
    case "buddy_request_accepted":
      return "/community/buddynet";
    case "match_candidate_available":
      return "/my-diving/dive-activity/matches";
    case "job_failed":
      return options.isAdmin ? "/admin/operations?tab=queues&queue=jobs" : null;
    case "job_completed":
      return null;
    default:
      return null;
  }
}

/**
 * Safely open a notification target. Never throws. Returns `true` if a
 * navigation was performed, `false` if the click should only mark-as-read.
 */
export function openNotificationTarget(
  item: NotificationInboxItem,
  navigate: NavigateFunction,
  options: NavigateOptions = {},
): boolean {
  const target = resolveNotificationTarget(item, options);
  if (!target) return false;
  try {
    navigate(target);
    return true;
  } catch {
    // react-router throws synchronously only on misuse; degrade gracefully.
    return false;
  }
}